import React, { useState } from 'react';
import { formatWeekRange } from '../utils/helpers';

export default function DigestTab({ digestText, digestLoading, fetchDigest, user }) {
  const [digestWeekStart, setDigestWeekStart] = useState(null);

  // Get current week display info
  const getCurrentWeekInfo = () => {
    if (digestWeekStart) {
      const mondayDate = new Date(digestWeekStart);
      return {
        isCurrentWeek: false,
        label: formatWeekRange(mondayDate)
      };
    } else {
      const today = new Date();
      const currentWeekday = today.getDay();
      const daysUntilMonday = currentWeekday === 0 ? 1 : 7 - currentWeekday + 1;
      const nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      return {
        isCurrentWeek: true,
        label: `Upcoming Week (${formatWeekRange(nextMonday)})`
      };
    }
  };

  // Navigate to previous week
  const goToPreviousWeek = () => {
    const currentStart = digestWeekStart ? new Date(digestWeekStart) : (() => {
      const today = new Date();
      const currentWeekday = today.getDay();
      const daysUntilMonday = currentWeekday === 0 ? 1 : 7 - currentWeekday + 1;
      const nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      return nextMonday;
    })();
    
    const previousWeek = new Date(currentStart);
    previousWeek.setDate(previousWeek.getDate() - 7);
    const newWeekStart = previousWeek.toISOString().split('T')[0];
    setDigestWeekStart(newWeekStart);
    fetchDigest(newWeekStart);
  };

  // Navigate to next week
  const goToNextWeek = () => {
    const currentStart = digestWeekStart ? new Date(digestWeekStart) : (() => {
      const today = new Date();
      const currentWeekday = today.getDay();
      const daysUntilMonday = currentWeekday === 0 ? 1 : 7 - currentWeekday + 1;
      const nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      return nextMonday;
    })();
    
    const nextWeek = new Date(currentStart);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const newWeekStart = nextWeek.toISOString().split('T')[0];
    setDigestWeekStart(newWeekStart);
    fetchDigest(newWeekStart);
  };

  // Reset to default week (next Monday)
  const goToDefaultWeek = () => {
    setDigestWeekStart(null);
    fetchDigest(null);
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <h2 className="content-title" style={{ marginBottom: 12 }}>Weekly PTO Digest</h2>
      
      {/* Week Navigation Controls */}
      <div style={{
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: 16, 
        padding: '12px 16px', 
        background: '#F6F4F0', 
        borderRadius: 8, 
        border: '1px solid #e5e7eb'
      }}>
        <button 
          onClick={goToPreviousWeek}
          disabled={digestLoading}
          style={{
            background: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            padding: '8px 12px',
            cursor: digestLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            color: digestLoading ? 'var(--symbolica-muted)' : 'var(--symbolica-black)'
          }}
        >
          ← Previous Week
        </button>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4
        }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--symbolica-black)' }}>
            {getCurrentWeekInfo().label}
          </div>
          {!getCurrentWeekInfo().isCurrentWeek && (
            <button 
              onClick={goToDefaultWeek}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--symbolica-black)',
                fontSize: '12px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Return to Current Week
            </button>
          )}
        </div>
        
        <button 
          onClick={goToNextWeek}
          disabled={digestLoading}
          style={{
            background: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            padding: '8px 12px',
            cursor: digestLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            color: digestLoading ? 'var(--symbolica-muted)' : 'var(--symbolica-black)'
          }}
        >
          Next Week →
        </button>
      </div>

      {/* Digest Content */}
      <div style={{ position: 'relative' }}>
        {digestLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            borderRadius: 6
          }}>
            <div style={{
              width: 20,
              height: 20,
              border: '2px solid #e5e7eb',
              borderTop: '2px solid var(--symbolica-black)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <textarea 
            value={digestText} 
            readOnly 
            style={{
              width: '100%',
              height: 300,
              fontFamily: 'monospace',
              opacity: digestLoading ? 0.5 : 1,
              transition: 'opacity 0.2s',
              resize: 'vertical'
            }} 
          />
          <button
            onClick={() => {
              navigator.clipboard.writeText(digestText).then(() => {
                const button = document.activeElement;
                const originalText = button.textContent;
                button.textContent = '✓';
                button.style.color = '#059669';
                setTimeout(() => {
                  button.textContent = originalText;
                  button.style.color = 'var(--symbolica-black)';
                }, 1000);
              }).catch(err => {
                console.error('Failed to copy text: ', err);
                alert('Failed to copy to clipboard');
              });
            }}
            disabled={digestLoading || !digestText}
            style={{
              background: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              padding: '8px 12px',
              cursor: digestLoading || !digestText ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              color: 'var(--symbolica-black)',
              opacity: digestLoading || !digestText ? 0.5 : 1,
              transition: 'all 0.2s',
              minWidth: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Copy digest to clipboard"
          >
            
          </button>
        </div>
      </div>
      
      <p style={{ fontSize: 13, color: 'var(--symbolica-muted)' }}>Copy the digest text to share in Slack or other channels.</p>
    </div>
  );
} 